import React, { useState, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { saveUserBet } from '../services/userDataService'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import {
  LEG_OPTIONS,
  getEVForLegs, getPayout, getThresholdOdds,
} from './pickEmConfig'
import { settlementFields, legRecord } from '../TRACKER_PAGE/settlement'
import '../styles/app-multi-builder.css'
import { playerImageUrlFromSelection as getPlayerImageUrl } from '../config/playerImages'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatGameTime = (ts) => {
  const d = new Date(ts + 'Z')
  const days   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  let hours = d.getHours()
  const mins = d.getMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}  ·  ${hours}:${mins} ${ampm}`
}

const evClass = (ev) => {
  if (ev >= 10) return 'bc-mb-ev bc-mb-ev-high'
  if (ev >= 5)  return 'bc-mb-ev bc-mb-ev-mid'
  return 'bc-mb-ev bc-mb-ev-low'
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PickEmMultiBuilder({
  isOpen,
  onClose,
  rawOpportunities,
  selectedLegs,
  onLegsChange,
  mobile = false,
}) {
  const { user } = useAuth()

  const [activeTab,    setActiveTab]    = useState('build')
  const [stake,        setStake]        = useState('')
  const [autoBuildLegs, setAutoBuildLegs] = useState('5')
  const [autoError,    setAutoError]    = useState('')

  // ─── Stats ────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    if (selectedLegs.length === 0) return { combinedFairOdds: 0, payout: 0, combinedEV: 0, potentialProfit: 0 }
    const legCount   = selectedLegs.length
    const payout     = getPayout(legCount) || 0
    const combinedFairOdds = selectedLegs.reduce((t, l) => t * l.fair_odds, 1)
    const combinedEV = combinedFairOdds > 0 ? ((payout / combinedFairOdds) - 1) * 100 : 0
    const s          = parseFloat(stake) || 0
    return { combinedFairOdds, payout, combinedEV, potentialProfit: s * (payout - 1) }
  }, [selectedLegs, stake])

  // Group legs by game for display
  const legGroups = useMemo(() => {
    const map = new Map()
    selectedLegs.forEach(leg => {
      const key = `${leg.commence_time}|${leg.match}`
      if (!map.has(key)) map.set(key, { match: leg.match, commence_time: leg.commence_time, legs: [] })
      map.get(key).legs.push(leg)
    })
    return [...map.values()].sort((a, b) => new Date(a.commence_time + 'Z') - new Date(b.commence_time + 'Z'))
  }, [selectedLegs])

  const removeLeg  = (key) => onLegsChange(selectedLegs.filter(l => l._key !== key))
  const clearAll   = () => { onLegsChange([]); setStake('') }

  // ─── Auto build ───────────────────────────────────────────────────────────

  const autoBuild = () => {
    setAutoError('')
    const targetLegs = parseInt(autoBuildLegs)

    const sorted = [...rawOpportunities].sort((a, b) => {
      const aEV = getEVForLegs(a.ev_by_legs, autoBuildLegs).ev || 0
      const bEV = getEVForLegs(b.ev_by_legs, autoBuildLegs).ev || 0
      return bEV - aEV
    })

    const selected   = []
    const usedPlayers = new Set()
    const usedMatches = new Set()

    for (const opp of sorted) {
      if (selected.length >= targetLegs) break
      if (usedPlayers.has(opp.player_name)) continue
      // On the last slot try to avoid all legs being in the same game
      if (selected.length === targetLegs - 1 && usedMatches.size === 1 && usedMatches.has(opp.match)) continue

      selected.push({
        _key:          opp._id,
        player_name:   opp.player_name,
        selection:     opp.selection,
        match:         opp.match,
        market_type:   opp.market_type,
        sport:         opp.sport,
        commence_time: opp.commence_time,
        fair_odds:     opp.fair_odds,
        ev_by_legs:    opp.ev_by_legs,
        game_url:      opp.game_url || null,
        // Same settlement fields the manually added legs carry, so an
        // auto-built multi is no less settleable than a hand-built one.
        ...settlementFields(opp),
      })
      usedPlayers.add(opp.player_name)
      usedMatches.add(opp.match)
    }

    if (selected.length < targetLegs) {
      setAutoError(`Not enough unique players (found ${selected.length}/${targetLegs})`)
      return
    }

    onLegsChange(selected)
    setActiveTab('build')
  }

  // ─── Save to tracker ──────────────────────────────────────────────────────

  const saveToTracker = async () => {
    if (!user?.id || selectedLegs.length < 2) return
    const s = parseFloat(stake)
    if (!s || s <= 0) return
    const { payout, combinedEV } = stats
    const bet = {
      type:          'EV',
      bet_type_tag:  'dabble_pickem',
      sport:         selectedLegs[0].sport,
      match:         `${selectedLegs.length}-Leg Pick'Em`,
      selection:     selectedLegs.map(l => l.selection).join(', '),
      bookmaker:     'Dabble',
      market_type:   'pickem_multi',
      // Each leg keeps the structured fields a single carries, which is what
      // lets settlement resolve them one game at a time. Without this the multi
      // is only a sentence and can never be settled automatically.
      legs:          selectedLegs.map(legRecord),
      odds:          payout,
      stake:         s,
      ev_percentage: parseFloat(combinedEV.toFixed(2)),
      status:        'pending',
      actual_profit: null,
      placed_at:     new Date().toISOString(),
      settled_at:    null,
    }
    const { error } = await saveUserBet(user.id, bet)
    if (!error) { clearAll(); onClose() }
  }

  if (!isOpen) return null

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <div className="bc-mb-overlay" onClick={onClose} />
      <div className={`bc-mb-modal${mobile ? ' bc-mb-modal-mobile' : ''}`} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="bc-mb-header">
          <div className="bc-mb-header-left">
            <h2 className="bc-mb-title">Pick'Em Multi Builder</h2>
          </div>
          <button className="bc-mb-close" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="bc-mb-tabs">
          <button className={`bc-mb-tab${activeTab === 'build' ? ' active' : ''}`} onClick={() => setActiveTab('build')}>
            Build
          </button>
          <button className={`bc-mb-tab${activeTab === 'auto' ? ' active' : ''}`} onClick={() => setActiveTab('auto')}>
            Auto Build
          </button>
        </div>

        <div className="bc-mb-body">

          {/* ── BUILD TAB ── */}
          {activeTab === 'build' && (
            selectedLegs.length === 0 ? (
              <div className="bc-mb-empty">
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="4"/>
                </svg>
                <p className="bc-mb-empty-title">No legs added yet</p>
                <p className="bc-mb-empty-sub">Tap the square on any row to add a leg, or use <strong>Auto Build</strong></p>
              </div>
            ) : (
              <div className="bc-mb-build-content">

                {/* Legs + banner */}
                <div className="bc-mb-legs-with-banner">
                  <div className="bc-mb-legs">
                    {legGroups.map(group => (
                      <div key={`${group.commence_time}|${group.match}`} className="bc-mb-leg-group">

                        <div className="bc-mb-leg-group-header">
                          <span className="bc-mb-leg-group-match">{group.match}</span>
                          <span className="bc-mb-leg-group-time">{formatGameTime(group.commence_time)}</span>
                        </div>

                        {group.legs.map((leg, i) => {
                          const parts  = leg.selection.match(/^(.+?)\s+(over|under)\s+(.+)$/i)
                          const player = parts ? parts[1] : (leg.player_name || leg.selection)
                          const dir    = parts ? parts[2].toLowerCase() : null
                          const line   = parts ? parts[3] : null
                          const ev        = getEVForLegs(leg.ev_by_legs, String(selectedLegs.length)).ev
                          const threshold = getThresholdOdds(String(selectedLegs.length))

                          return (
                            <div key={leg._key} className="bc-mb-leg">

                              {/* Avatar */}
                              <div className="bcm-avatar-wrap" style={{ flexShrink: 0 }}>
                                <img
                                  src={getPlayerImageUrl(leg.selection, leg.sport)}
                                  alt=""
                                  className="bcm-avatar"
                                  onLoad={e  => { e.target.style.opacity = '1'; e.target.parentNode.classList.remove('bc-avatar-missing') }}
                                  onError={e => { e.target.style.opacity = '0'; e.target.parentNode.classList.add('bc-avatar-missing') }}
                                />
                              </div>

                              {/* Info */}
                              <div className="bc-mb-leg-body">
                                <div className="bc-mb-leg-row1">
                                  <span className="bc-mb-leg-player">{player}</span>
                                </div>
                                <div className="bc-mb-leg-row2">
                                  {dir && (
                                    <span className={`bc-mb-leg-dir ${dir}`}>
                                      {dir.charAt(0).toUpperCase() + dir.slice(1)} {line}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Right: threshold odds + EV + remove */}
                              <div className="bc-mb-leg-right">
                                {!mobile && (
                                  <img
                                    src={BOOKMAKER_LOGOS['Dabble']}
                                    alt="Dabble"
                                    className="bc-mb-leg-book-logo"
                                    title="Dabble"
                                    onClick={() => leg.game_url && window.open(leg.game_url, '_blank')}
                                    style={{ cursor: leg.game_url ? 'pointer' : 'default' }}
                                  />
                                )}
                                <span className="bc-mb-result-odds">{threshold?.toFixed(3) ?? '—'}</span>
                                {ev !== null && (
                                  <span className={`${evClass(ev)} bc-mb-leg-ev-badge`}>
                                    {ev > 0 ? '+' : ''}{ev.toFixed(1)}%
                                  </span>
                                )}
                                <button className="bc-mb-leg-remove" onClick={() => removeLeg(leg._key)}>×</button>
                              </div>

                            </div>
                          )
                        })}
                      </div>
                    ))}
                  </div>

                  {/* Stats banner */}
                  <div className="bc-mb-banner">
                    <div className="bc-mb-banner-left">
                      {BOOKMAKER_LOGOS['Dabble'] && (
                        <img src={BOOKMAKER_LOGOS['Dabble']} alt="Dabble" className="bc-mb-stats-bar-logo" />
                      )}
                      <span className="bc-mb-stats-bar-book-name">Dabble Pick'Em</span>
                      {!mobile && (
                        <>
                          <div className="bc-mb-banner-vdivider" />
                          <span className="bc-mb-banner-odds">{stats.payout}x payout</span>
                        </>
                      )}
                    </div>
                    <div className="bc-mb-banner-right">
                      <span className={`bc-mb-ev ${stats.combinedEV >= 10 ? 'bc-mb-ev-high' : stats.combinedEV >= 5 ? 'bc-mb-ev-mid' : 'bc-mb-ev-low'}`}>
                        {stats.combinedEV > 0 ? '+' : ''}{stats.combinedEV.toFixed(1)}%
                      </span>
                      <span className="bc-mb-result-leg-badge">{selectedLegs.length} Leg Pick'Em</span>
                    </div>
                  </div>
                </div>

                {/* Stake + profit */}
                <div className="bc-mb-inputs" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="bc-mb-input-group">
                    <label className="bc-mb-input-label">Stake</label>
                    <div className="bc-mb-input-prefix">
                      <span className="bc-mb-prefix">$</span>
                      <input
                        type="number"
                        step="1"
                        placeholder="0"
                        value={stake}
                        onChange={e => setStake(e.target.value)}
                        className="bc-mb-input bc-mb-input-has-prefix"
                      />
                    </div>
                  </div>
                  <div className="bc-mb-input-group">
                    <label className="bc-mb-input-label">Potential Profit</label>
                    <div className={`bc-mb-profit-display${parseFloat(stake) > 0 ? ' has-value' : ''}`}>
                      <span className="bc-mb-profit-val">
                        {parseFloat(stake) > 0 ? `$${stats.potentialProfit.toFixed(2)}` : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="bc-mb-build-actions">
                  <button className="bc-btn ghost" onClick={clearAll}>Clear All</button>
                  <button
                    className="bc-btn primary"
                    onClick={saveToTracker}
                    disabled={selectedLegs.length < 2 || !stake || parseFloat(stake) <= 0}
                  >
                    Save to Tracker
                  </button>
                </div>

              </div>
            )
          )}

          {/* ── AUTO BUILD TAB ── */}
          {activeTab === 'auto' && (
            <div className="bc-mb-auto-content">

              <div className="bc-filter-section">
                <span className="bc-filter-label">Number of Legs</span>
                <select
                  className="bc-select"
                  value={autoBuildLegs}
                  onChange={e => setAutoBuildLegs(e.target.value)}
                  style={{ marginTop: '6px' }}
                >
                  {LEG_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} · {opt.payout}x payout
                    </option>
                  ))}
                </select>
              </div>


              {autoError && <div className="bc-mb-auto-error">{autoError}</div>}

              <div className="bc-mb-auto-footer">
                <button className="bc-btn primary bc-mb-generate-btn" onClick={autoBuild}>
                  Auto Build {autoBuildLegs} Legs
                </button>
              </div>

            </div>
          )}

        </div>
      </div>
    </>
  )
}
